import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-scroll";
import { Link as RouterLink, useLocation } from "react-router-dom";
// Icons
import { FaRocket, FaChevronDown } from "react-icons/fa6";
// Components
import Sidebar from "./Sidebar";
import Backdrop from "../Elements/Backdrop";
// Assets
import LogoIcon from "../../assets/optimized/logo.webp";
import BurgerIcon from "../../assets/svg/BurgerIcon";

export default function TopNavbar() {
  const [y, setY] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [navbarHeight, setNavbarHeight] = useState("90px");
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const location = useLocation();
  
  // Check if we're on the landing page
  const isLandingPage = location.pathname === '/';
  // Check if we're on the launch event page
  const isLaunchEventPage = location.pathname === '/launch-event';

  // Get responsive navbar height
  const getNavbarHeight = () => {
    if (typeof window === 'undefined') return "90px";
    const width = window.innerWidth;
    if (width <= 480) return "58px";
    if (width <= 768) return "68px";
    if (width <= 1024) return "75px";
    if (width <= 1200) return "80px";
    return "90px";
  };

  const updateNavbarHeight = () => {
    setNavbarHeight(getNavbarHeight());
  };
  
  const toggleSidebar = () => {
    console.log('Toggle sidebar called, current state:', sidebarOpen);
    setSidebarOpen(prev => {
      const newState = !prev;
      
      // Lock/unlock body scroll when sidebar opens/closes
      if (typeof document !== 'undefined') {
        if (newState) {
          // Lock body scroll when sidebar opens
          document.body.style.overflow = 'hidden';
          document.body.style.position = 'fixed';
          document.body.style.width = '100%';
        } else {
          // Unlock body scroll when sidebar closes
          document.body.style.overflow = '';
          document.body.style.position = '';
          document.body.style.width = '';
        }
      }
      
      return newState;
    });
  };

  useEffect(() => {
    // Detect mobile device
    const checkMobile = () => {
      const mobile = typeof window !== 'undefined' && (
        window.innerWidth <= 768 || 
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        'ontouchstart' in window
      );
      setIsMobile(mobile);
    };

    checkMobile();
    updateNavbarHeight();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('resize', updateNavbarHeight);
    
    console.log('Mobile detected:', isMobile, 'Initial sidebar state:', sidebarOpen);
    
    // Completely skip ALL event listeners on mobile
    if (isMobile) {
      console.log('Mobile detected - all event listeners disabled');
      setY(0); // Fixed state for mobile
      return () => {
        window.removeEventListener('resize', checkMobile);
        window.removeEventListener('resize', updateNavbarHeight);
      };
    }
    
    // Desktop-only scroll handling
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('resize', updateNavbarHeight);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up body scroll when sidebar closes or component unmounts
  useEffect(() => {
    return () => {
      // Always unlock body scroll on cleanup
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      }
    };
  }, []);

  // Handle body scroll when sidebar state changes
  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (sidebarOpen) {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      } else {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      }
    }
  }, [sidebarOpen]);

  // Navigation component
  const NavItem = ({ to, children, className = "semiBold font15 pointer" }) => {
    if (isLandingPage) {
      // Use react-scroll Link for landing page
      return (
        <li className={className}>
          <Link 
            activeClass="active" 
            style={{ 
              padding: "10px 15px",
              color: y > 100 || isMobile || isLaunchEventPage ? "#0b093b" : "white",
              textDecoration: "none",
              fontWeight: "700",
              fontSize: "15px",
              display: "block",
              transition: "all 0.3s ease",
              textShadow: (!(y > 100 || isMobile || isLaunchEventPage)) ? "0 2px 4px rgba(0, 0, 0, 0.3)" : "none"
            }} 
            to={to} 
            spy={true} 
            smooth={true} 
            offset={-80}
          >
            {children}
          </Link>
        </li>
      );
    } else {
      // Use Router Link to navigate back to landing page with hash
      return (
        <li className={className}>
          <RouterLink 
            to={`/#${to}`} 
            style={{ 
              padding: "10px 15px", 
              color: y > 100 || isMobile || isLaunchEventPage ? "#0b093b" : "white",
              textDecoration: "none",
              fontWeight: "700",
              fontSize: "15px",
              display: "block",
              transition: "all 0.3s ease",
              textShadow: (!(y > 100 || isMobile || isLaunchEventPage)) ? "0 2px 4px rgba(0, 0, 0, 0.3)" : "none"
            }}
          >
            {children}
          </RouterLink>
        </li>
      );
    }
  };

  return (
    <>
      {/* Only render Sidebar when needed on mobile */}
      {(sidebarOpen && isMobile) && (
        <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      )}
      {sidebarOpen && <Backdrop toggleSidebar={toggleSidebar} />}
      <Wrapper className="flexCenter" style={{
        height: navbarHeight,
        background: isMobile 
          ? "#079487" 
          : (y > 100 || isLaunchEventPage) 
            ? "rgba(255, 255, 255, 0.95)" 
            : "transparent",
        backdropFilter: (y > 100 || isLaunchEventPage) && !isMobile ? "blur(10px)" : "none",
        boxShadow: isMobile ? "none" : (y > 100 || isLaunchEventPage) ? "0 2px 20px rgba(7, 148, 135, 0.1)" : "none",
        transition: isMobile ? "none" : "all 0.3s ease"
      }}>
        <NavInner>
          <NavContent>
            <RouterLink to="/" className="pointer flexNullCenter">
              <LogoWrapper sidebarOpen={sidebarOpen}>
                <LogoImage 
                  src={LogoIcon}
                  alt="logo"
                  width={153}
                  height={85}
                />
              </LogoWrapper>
            </RouterLink>
            <BurderWrapper 
              className="pointer" 
              onClick={toggleSidebar}
              isOpen={sidebarOpen}
              aria-label={sidebarOpen ? "Close navigation menu" : "Open navigation menu"}
            >
              <BurgerIcon />
            </BurderWrapper>
            <UlWrapper className="flexNullCenter">
              <NavItem to="home">Home</NavItem>
              
              {/* About Dropdown */}
              <DropdownWrapper 
                onMouseEnter={() => setAboutDropdownOpen(true)}
                onMouseLeave={() => setAboutDropdownOpen(false)}
              >
                <DropdownTrigger className="semiBold font15 pointer">
                  <Link 
                    activeClass="active" 
                    style={{ 
                      padding: "10px 15px",
                      color: y > 100 || isMobile || isLaunchEventPage ? "#0b093b" : "white",
                      textDecoration: "none",
                      fontWeight: "700",
                      fontSize: "15px",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      transition: "all 0.3s ease",
                      textShadow: (!(y > 100 || isMobile || isLaunchEventPage)) ? "0 2px 4px rgba(0, 0, 0, 0.3)" : "none"
                    }} 
                    to="about" 
                    spy={true} 
                    smooth={true} 
                    offset={-80}
                  >
                    About 
                    <FaChevronDown 
                      style={{ 
                        fontSize: "10px", 
                        transition: "transform 0.3s ease",
                        transform: aboutDropdownOpen ? "rotate(180deg)" : "rotate(0deg)"
                      }} 
                    />
                  </Link>
                </DropdownTrigger>
                <DropdownMenu isOpen={aboutDropdownOpen}>
                  <DropdownItem>
                    <NavItem to="services">Services</NavItem>
                  </DropdownItem>
                  <DropdownItem>
                    <NavItem to="impact">Impact</NavItem>
                  </DropdownItem>
                  <DropdownItem>
                    <RouterLink 
                      to="/launch-event" 
                      style={{
                        color: "#333",
                        textDecoration: 'none',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        padding: "12px 20px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                    >
                      <FaRocket style={{ fontSize: '12px' }} />
                      Anton Tech Official Launch Event
                    </RouterLink>
                  </DropdownItem>
                </DropdownMenu>
              </DropdownWrapper>
              
              <li className="semiBold font15 pointer">
                <RouterLink 
                  to="/who-is-anton-for" 
                  style={{
                    color: y > 100 || isMobile || isLaunchEventPage ? "#0b093b" : "white",
                    textDecoration: 'none',
                    fontWeight: '700',
                    transition: 'all 0.3s ease',
                    padding: "10px 15px",
                    display: "block",
                    textShadow: (!(y > 100 || isMobile || isLaunchEventPage)) ? "0 2px 4px rgba(0, 0, 0, 0.3)" : "none"
                  }}
                >
                  Who is Anton for?
                </RouterLink>
              </li>
              
              <NavItem to="partners">Partners</NavItem>
              <li className="semiBold font15 pointer">
                <RouterLink 
                  to="/developer-tools" 
                  style={{
                    color: y > 100 || isMobile || isLaunchEventPage ? "#0b093b" : "white",
                    textDecoration: 'none',
                    fontWeight: '700',
                    transition: 'all 0.3s ease',
                    padding: "10px 15px",
                    display: "block",
                    textShadow: (!(y > 100 || isMobile || isLaunchEventPage)) ? "0 2px 4px rgba(0, 0, 0, 0.3)" : "none"
                  }}
                >
                  Developer Tools
                </RouterLink>
              </li>
              <NavItem to="contact">Contact</NavItem>
              <li className="semiBold font15 pointer">
                <GetStartedBtn 
                  href="https://app.anton.co.bw/login" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  y={y}
                  isMobile={isMobile}
                >
                  Access Project Anton
                </GetStartedBtn>
              </li>
            </UlWrapper>
          </NavContent>
        </NavInner>
      </Wrapper>
    </>
  );
}

const Wrapper = styled.nav`
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
`;

const NavInner = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  
  @media (max-width: 859px) {
    padding: 0 12px;
  }

  @media (max-width: 480px) {
    padding: 0 10px;
  }
`;

const NavContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0;
  height: 90px;
  gap: 12px;
  
  @media (max-width: 1200px) {
    height: 80px;
    padding: 0.5rem 0;
  }

  @media (max-width: 1024px) {
    height: 75px;
    gap: 10px;
  }

  @media (max-width: 768px) {
    height: 68px;
    gap: 8px;
    padding: 0.4rem 0;
  }

  @media (max-width: 480px) {
    height: 58px;
    gap: 8px;
    padding: 0.3rem 0;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: auto;
  
  @media (max-width: 480px) {
    padding: 2px 0;
    display: ${props => props.sidebarOpen ? 'none' : 'flex'};
  }

  @media (max-width: 768px) {
    display: ${props => props.sidebarOpen ? 'none' : 'flex'};
  }
`;

const LogoImage = styled.img`
  height: 85px;
  width: auto;
  transition: all 0.3s ease;
  flex-shrink: 0;
  object-fit: contain;
  
  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 1200px) {
    height: 73px;
  }

  @media (max-width: 1024px) {
    height: 66px;
  }

  @media (max-width: 768px) {
    height: 55px;
  }

  @media (max-width: 480px) {
    height: 42px;
  }
`;

const BurderWrapper = styled.button`
  outline: none;
  border: 0px;
  background-color: ${props => props.isOpen ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.15)'};
  height: 48px;
  width: 48px;
  min-width: 48px;
  padding: 10px;
  display: none;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s ease;
  color: white;
  position: relative;
  z-index: 1000;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 26px;
    height: 20px;
    stroke-width: 2.5;
  }
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 1024px) {
    display: flex;
  }

  @media (max-width: 768px) {
    height: 44px;
    width: 44px;
    min-width: 44px;
    color: #000;
    background-color: rgba(255, 255, 255, 0.9);

    &:hover {
      background-color: rgba(255, 255, 255, 1);
    }
    
    svg {
      width: 24px;
      height: 18px;
      stroke: #000;
    }
  }

  @media (max-width: 480px) {
    height: 42px;
    width: 42px;
    min-width: 42px;
    
    svg {
      width: 22px;
      height: 17px;
    }
  }
`;

const UlWrapper = styled.ul`
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  
  @media (max-width: 1400px) {
    gap: 15px;
  }
  
  @media (max-width: 1200px) {
    gap: 10px;
  }
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const GetStartedBtn = styled.a`
  border: 2px solid ${props => props.y > 100 || props.isMobile ? "#079487" : "rgba(255, 255, 255, 0.8)"};
  border-radius: 8px;
  padding: 12px 20px;
  color: ${props => props.y > 100 || props.isMobile ? "#079487" : "white"};
  background: ${props => props.y > 100 || props.isMobile ? "linear-gradient(135deg, rgba(7, 148, 135, 0.1) 0%, rgba(7, 148, 135, 0.05) 100%)" : "linear-gradient(135deg, rgba(7, 148, 135, 0.2) 0%, rgba(7, 148, 135, 0.1) 100%)"};
  text-decoration: none;
  font-weight: 700;
  font-size: 14px;
  transition: all 0.3s ease;
  display: block;
  text-align: center;
  backdrop-filter: ${props => props.y <= 100 && !props.isMobile ? "blur(10px)" : "none"};
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  
  /* Pulsating animation */
  animation: buttonPulse 2.5s infinite;
  
  @keyframes buttonPulse {
    0% {
      box-shadow: 0 0 5px ${props => props.y > 100 || props.isMobile ? "rgba(7, 148, 135, 0.4)" : "rgba(255, 255, 255, 0.3)"};
    }
    50% {
      box-shadow: 0 0 20px ${props => props.y > 100 || props.isMobile ? "rgba(7, 148, 135, 0.8)" : "rgba(255, 255, 255, 0.6)"}, 
                  0 0 30px ${props => props.y > 100 || props.isMobile ? "rgba(7, 148, 135, 0.6)" : "rgba(255, 255, 255, 0.4)"};
      border-color: ${props => props.y > 100 || props.isMobile ? "rgba(7, 148, 135, 0.8)" : "rgba(255, 255, 255, 1)"};
    }
    100% {
      box-shadow: 0 0 5px ${props => props.y > 100 || props.isMobile ? "rgba(7, 148, 135, 0.4)" : "rgba(255, 255, 255, 0.3)"};
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    background: linear-gradient(135deg, #079487 0%, #0a7c73 100%);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(7, 148, 135, 0.6), 0 0 40px rgba(7, 148, 135, 0.4);
    border-color: #079487;
    animation: none; /* Stop pulsing on hover */
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Dropdown styled components
const DropdownWrapper = styled.li`
  position: relative;
  display: inline-block;
`;

const DropdownTrigger = styled.li`
  cursor: pointer;
  position: relative;
`;

const DropdownMenu = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.3s ease;
  z-index: 1000;
  padding: 8px 0;
  margin: 0;
  list-style: none;
  
  @media (max-width: 859px) {
    display: none;
  }
`;

const DropdownItem = styled.li`
  margin: 0;
  
  a {
    color: #333 !important;
    padding: 12px 20px !important;
    display: block !important;
    transition: all 0.3s ease !important;
    
    &:hover {
      background: #f8f9fa;
      color: #079487 !important;
    }
  }
`;
